import axios from 'axios'
import { Preferences } from '@capacitor/preferences'
import { captureException } from "@sentry/browser";

// Helper functions for preferences
async function getPreference(key) {
  const result = await Preferences.get({ key });
  return result.value ? JSON.parse(result.value) : null;
}

async function setPreference(key, value) {
  return await Preferences.set({ key, value: JSON.stringify(value) });
}

async function removePreference(key) {
  return await Preferences.remove({ key });
}

// state
export const state = {
  symptomLogs: {}, // { 'YYYY-MM-DD': { tracking_date: 'YYYY-MM-DD', answers: {...} } }
  isLoading: false,
  loadingDates: {}, // { 'YYYY-MM-DD': boolean }
  lastFetched: {}, // { 'YYYY-MM-DD': timestamp } 
  cacheDuration: 5 * 60 * 1000 // 5 minutes
}

// Initialize state from preferences
getPreference('symptomLogs').then((logs) => {
  if (logs) {
    state.symptomLogs = logs
  }
})

getPreference('symptomLogsLastFetched').then((fetched) => {
  if (fetched) {
    state.lastFetched = fetched
  }
})

// getters
export const getters = {
  symptomLogs: state => state.symptomLogs,
  isLoading: state => state.isLoading,
  loadingDates: state => state.loadingDates,
  lastFetched: state => state.lastFetched,
  
  // Get symptom log for specific date
  getSymptomLogForDate: (state) => (date) => {
    return state.symptomLogs[date] || null
  },
  
  // Check if date is loading
  isDateLoading: (state) => (date) => {
    return !!state.loadingDates[date]
  },
  
  // Check if data for date needs refresh
  shouldRefreshDate: (state) => (date) => {
    const lastFetched = state.lastFetched[date]
    if (!lastFetched) return true
    return Date.now() - lastFetched > state.cacheDuration
  }
}

// mutations
export const mutations = {
  async SET_SYMPTOM_LOGS(state, logs) {
    state.symptomLogs = logs
    await setPreference('symptomLogs', logs)
  },
  
  async SET_SYMPTOM_LOG_FOR_DATE(state, { date, log }) {
    state.symptomLogs = { ...state.symptomLogs, [date]: log }
    await setPreference('symptomLogs', state.symptomLogs)
  },
  
  async REMOVE_SYMPTOM_LOG_FOR_DATE(state, date) {
    const newLogs = { ...state.symptomLogs }
    delete newLogs[date]
    state.symptomLogs = newLogs
    
    const newLastFetched = { ...state.lastFetched }
    delete newLastFetched[date]
    state.lastFetched = newLastFetched
    
    await setPreference('symptomLogs', state.symptomLogs)
    await setPreference('symptomLogsLastFetched', state.lastFetched)
  },
  
  SET_LOADING(state, loading) {
    state.isLoading = loading
  },
  
  SET_DATE_LOADING(state, { date, loading }) {
    if (loading) {
      state.loadingDates = { ...state.loadingDates, [date]: true }
    } else {
      const newLoadingDates = { ...state.loadingDates }
      delete newLoadingDates[date]
      state.loadingDates = newLoadingDates
    }
  },
  
  async SET_LAST_FETCHED_FOR_DATE(state, { date, timestamp }) {
    state.lastFetched = { ...state.lastFetched, [date]: timestamp }
    await setPreference('symptomLogsLastFetched', state.lastFetched)
  },
  
  async CLEAR_SYMPTOM_LOGS(state) {
    state.symptomLogs = {}
    state.lastFetched = {}
    state.loadingDates = {}
    await removePreference('symptomLogs')
    await removePreference('symptomLogsLastFetched')
  }
}

// actions
export const actions = {
  async fetchSymptomLogForDate({ commit, state, getters }, { date, force = false }) {
    // Don't fetch if already loading or recently fetched, unless forced
    if (!force && (getters.isDateLoading(date) || !getters.shouldRefreshDate(date))) {
      return getters.getSymptomLogForDate(date)
    }

    commit('SET_DATE_LOADING', { date, loading: true })
    
    try {
      const response = await axios.get(`/habit-tracker-log/${date}`)
      const now = Date.now()
      
      // Check if response is an empty array (no log exists) or an object (log exists)
      if (Array.isArray(response.data) && response.data.length === 0) {
        // No log exists for this date
        await commit('SET_SYMPTOM_LOG_FOR_DATE', { date, log: null })
      } else if (response.data && response.data.data) {
        // Log exists, store it
        const logData = {
          tracking_date: date,
          answers: response.data.data
        }
        await commit('SET_SYMPTOM_LOG_FOR_DATE', { date, log: logData })
      } else {
        // Unexpected response format
        await commit('SET_SYMPTOM_LOG_FOR_DATE', { date, log: null })
      }
      
      await commit('SET_LAST_FETCHED_FOR_DATE', { date, timestamp: now })
      return getters.getSymptomLogForDate(date)
      
    } catch (error) {
      captureException(error)
      console.error('Error fetching symptom log for date:', date, error)
      // Set null to indicate no log exists (could be 404)
      await commit('SET_SYMPTOM_LOG_FOR_DATE', { date, log: null })
      throw error
    } finally {
      commit('SET_DATE_LOADING', { date, loading: false })
    }
  },

  async saveSymptomLog({ commit, dispatch }, { date, answers }) {
    commit('SET_DATE_LOADING', { date, loading: true })
    
    try {
      const payload = {
        tracking_date: date,
        answers
      }
      
      const response = await axios.post('/habit-tracker-logs', payload)
      
      // Update local store with saved data
      await commit('SET_SYMPTOM_LOG_FOR_DATE', { 
        date, 
        log: payload 
      })
      
      // Update last fetched timestamp
      await commit('SET_LAST_FETCHED_FOR_DATE', { 
        date, 
        timestamp: Date.now() 
      })
      
      // Return the full response which includes log and report
      return response.data
      
    } catch (error) {
      captureException(error)
      console.error('Error saving symptom log:', error)
      throw error
    } finally {
      commit('SET_DATE_LOADING', { date, loading: false })
    }
  },

  async updateSymptomLog({ dispatch }, { date, answers }) {
    // Same as save - the backend handles create/update
    return await dispatch('saveSymptomLog', { date, answers })
  },

  async clearAllSymptomLogs({ commit }) {
    await commit('CLEAR_SYMPTOM_LOGS')
  },

  async getSymptomLogsFromPreferences() {
    return await getPreference('symptomLogs')
  },

  // Batch operations
  async fetchMultipleDates({ dispatch }, dates) {
    const promises = dates.map(date => 
      dispatch('fetchSymptomLogForDate', { date, force: false })
    )
    return await Promise.all(promises)
  },

  // Helper to initialize empty answers structure based on translation keys
  getEmptyAnswersStructure(_, questionCategories) {
    const answers = {}
    for (const category of questionCategories) {
      answers[category] = {}
      // This would need to be populated with actual question keys
      // You might want to pass this structure from the component
    }
    return answers
  }
} 